import { handleActions } from 'redux-actions';
import update from 'immutability-helper';
import { filter, get, isNumber } from 'lodash';

import Actions from 'constants/actions';
import headerBackgroundImageSize from 'config/sectionHeader/headerBackgroundImageSize';
import headerContentAlignment from 'config/sectionHeader/headerContentAlignment';
import headerBackgroundDefaultCoords from '../config/sectionHeader/headerBackgroundDefaultCoords';
import isValidApiId from '../utils/isValidApiId';
import {
  defaultHeaderHeight,
  narrowHeaderHeight,
} from '../config/sectionHeader/headerHeightConfig';
import headerPageBoxTypes from '../config/sectionHeader/headerPageBoxTypes';
import { MEDIUM } from '../components/headerCustomizer/constants/headerLogoImageSizePresets';
import { LOGO_WITH_TEXT } from '../components/headerCustomizer/constants/headerLayoutPresets';

const empty = { object: {} };

const defaultState = null;

const reducer = handleActions(
  {
    [Actions.CLEAR_SECTION_HEADER]: (state, { payload }) =>
      update(state, {
        $unset: [payload],
      }),
    [Actions.CLEAR_SECTION_HEADERS]: state =>
      update(state, {
        $set: null,
      }),
    [Actions.SET_SECTION_HEADER]: (
      state,
      { payload: { sectionName, header } },
    ) =>
      update(state, {
        [sectionName]: { $set: header },
      }),
    [Actions.SET_SECTION_HEADERS]: (state, { payload: sectionHeaders }) =>
      update(state, {
        $set: sectionHeaders,
      }),
    [Actions.UPDATE_SECTION_HEADER]: (
      state,
      { payload: { sectionName, header } },
    ) =>
      update(state, {
        [sectionName]: { $merge: header },
      }),
    [Actions.UPDATE_SECTION_HEADERS]: (state, { payload: sectionHeaders }) =>
      update(state, {
        $merge: sectionHeaders,
      }),
  },
  defaultState,
);

export const getHeaderByUuid = (state, headerUuid) =>
  filter(state, header => header.clientUuid === headerUuid)[0];

export const getSectionHeader = (state, sectionName) => get(state, sectionName);

export const getHeaderData = state => state;

export const hasSectionHeader = (state, sectionName) =>
  get(state, sectionName) !== undefined;

export const hasPersistedSectionHeader = (state, sectionName) =>
  isValidApiId(get(state, `${sectionName}.id`));

export const getSectionHeaderBackgroundImage = (state, sectionName) =>
  get(getSectionHeader(state, sectionName), 'backgroundImage');

export const getSectionHeaderLogoImage = (state, sectionName) =>
  get(getSectionHeader(state, sectionName), 'logoImage');

export const getSectionHeaderBackgroundImageUrl = (state, sectionName) =>
  get(getSectionHeaderBackgroundImage(state, sectionName), 'src');

export const getSectionHeaderLogoImageUrl = (state, sectionName) =>
  get(getSectionHeaderLogoImage(state, sectionName), 'src');

export const getSectionHeaderLogoImageAlign = (state, sectionName) =>
  get(
    getSectionHeader(state, sectionName),
    'logoImageAlign',
    headerContentAlignment.left,
  );

export const getSectionHeaderLogoWithTextAlign = (state, sectionName) =>
  get(
    getSectionHeader(state, sectionName),
    'logoWithTextAlign',
    headerContentAlignment.left,
  );

export const getSectionHeaderBackgroundImageSize = (state, sectionName) =>
  get(
    getSectionHeader(state, sectionName),
    'backgroundImageSize',
    headerBackgroundImageSize.cover,
  );

export const getSectionHeaderHeight = (state, sectionName) =>
  get(getSectionHeader(state, sectionName), 'height', defaultHeaderHeight);

export const getSectionHeaderBackgroundColor = (state, sectionName) =>
  get(getSectionHeader(state, sectionName), 'backgroundColor');

export const isSectionHeaderContentEnabled = (state, sectionName) =>
  get(getSectionHeader(state, sectionName), 'contentEnabled');

export const getSectionHeaderLayout = (state, sectionName) =>
  get(getSectionHeader(state, sectionName), 'layout', LOGO_WITH_TEXT);

export const getSectionHeaderTextContent = (state, sectionName) =>
  get(getSectionHeader(state, sectionName), 'textContent');

export const getSectionHeaderClientUuid = (state, sectionName) =>
  get(getSectionHeader(state, sectionName), 'clientUuid');

export const getSectionHeaderLogoImageLinkUrl = (state, sectionName) =>
  get(getSectionHeaderLogoImage(state, sectionName), 'linkUrl');

export const getSectionHeaderLogoImageSizePreset = (state, sectionName) =>
  get(getSectionHeaderLogoImage(state, sectionName), 'sizePreset', MEDIUM);

export const getSectionHeaderLogoImageSize = (state, sectionName) =>
  get(getSectionHeaderLogoImage(state, sectionName), 'size');

export const isSectionHeaderPlaceholder = (state, sectionName) =>
  !(
    getSectionHeaderBackgroundColor(state, sectionName) ||
    getSectionHeaderBackgroundImageUrl(state, sectionName) ||
    isSectionHeaderContentEnabled(state, sectionName)
  );

export const isSectionHeaderFullWidth = (state, sectionName) =>
  get(getSectionHeader(state, sectionName), 'isFullWidth');

export const getSectionHeaderBackgroundCoords = (state, sectionName) => {
  const header = getSectionHeader(state, sectionName);
  const coords = get(header, 'backgroundCoords', empty.object);
  return isNumber(coords.x) && isNumber(coords.y)
    ? coords
    : headerBackgroundDefaultCoords;
};

export const getHeaderPageBoxType = (state, sectionName, isSurveyFullWidth) => {
  const isHeaderFullWidth = isSectionHeaderFullWidth(state, sectionName);
  const hasHeader = !!getSectionHeader(state, sectionName);
  const height = getSectionHeaderHeight(state, sectionName);

  if (!hasHeader) {
    return null;
  }

  if (!isHeaderFullWidth || isSurveyFullWidth) {
    return headerPageBoxTypes.trimmedTop;
  }

  if (isHeaderFullWidth && height >= narrowHeaderHeight) {
    return headerPageBoxTypes.lifted;
  }
};

export default reducer;
